/**
 * BCE Form Tracking Module
 *
 * Tracks the native TYPO3/Aimeos product inquiry form (modal on product
 * detail pages) and pushes GA4-ready events to the dataLayer. Successor of
 * the standalone native-form-tracking.js hosted on forms.binder-world.com —
 * remove that script tag once this module is deployed (the module stays
 * silent while the legacy script is still embedded, so rollout order is safe).
 *
 * dataLayer events (schema identical to binder-forms):
 *
 *   event: 'modal' — modal lifecycle
 *     modal_action: 'open'|'close'
 *     modal_name:   'product_inquiry'
 *     modal_product, modal_duration, modal_close_reason, modal_interactions
 *
 *   event: 'form' — form interaction
 *     form_action:  'start'|'submit'|'abandon'
 *     form_name:    'product_inquiry'
 *     form_product, form_duration, form_last_field,
 *     form_fields_filled, form_fields_empty, form_submit_attempts
 *     client_lead_id: UUID v4 (only on 'submit', server-confirmed)
 *
 * Lead tracking (client_lead_id + attribution):
 *   - One UUID v4 per modal session, injected as hidden field
 *     ai[user][clientLeadId] into the form → travels inside the same POST
 *     that TYPO3 forwards to the n8n webhook. The dataLayer submit event
 *     reads the SAME hidden field, so GA4 row and DWH row always match.
 *   - On every submit attempt the consent snapshot (ai[user][marketingConsent])
 *     and the attribution block (ai[user][attribution], JSON) are refreshed
 *     from Cookiebot / first-party cookies. Empty block → field removed.
 *
 * Bug fixes over native-form-tracking.js:
 *   - No false 'abandon' + 'modal close' on successful submit: product.js
 *     closes the form modal before opening the success modal; during that
 *     gap no .tingle-modal--visible exists. We detect the pending
 *     #popup-success (already in the DOM at that point) and hold off,
 *     with a timeout fallback in case the success modal never shows.
 *   - Close button on the success modal is recognized (delegated listener)
 *     → modal_close_reason 'button' instead of 'dom'.
 *   - Hidden fields (e.g. the China-only province select) are excluded
 *     from form_fields_filled / form_fields_empty.
 *
 * All tracking is fail-safe: an exception must never break the submit.
 *
 * @class BceFormTracking
 */
import { generateClientLeadId } from './lead-id.js';
import { getAttribution, getMarketingConsent } from './attribution.js';

const FORM_SELECTORS = [
    '#product-request-form',
    '#product_inquiry',
    'form[name="product_inquiry"]'
];

// Canonical slug used in dataLayer (matches binder-forms formConfigs)
const SLUG = 'product_inquiry';

// Hidden fields injected into the form POST (forwarded by TYPO3 to n8n)
const FIELD_LEAD_ID = 'ai[user][clientLeadId]';
const FIELD_CONSENT = 'ai[user][marketingConsent]';
const FIELD_ATTRIBUTION = 'ai[user][attribution]';

// If the form modal disappears and no success modal shows up within this
// window, the closing counts as an abandon (gap between the two modals is
// normally just one task, so 600ms is generous).
const SUCCESS_GAP_TIMEOUT = 600;

class BceFormTracking {
    constructor() {
        this.started = false;

        this.currentForm = null;
        this.currentModal = null;
        this.wasModalVisible = false;
        this.wasAnyModalVisible = false;
        this.gapTimer = null;

        this.resetState();

        // Bound handlers (stable references for add/removeEventListener)
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onCopy = this.onCopy.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onSubmitAttempt = this.onSubmitAttempt.bind(this);
        this.onDocumentMousedown = this.onDocumentMousedown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        this.scan = this.scan.bind(this);
    }

    /**
     * Start the global observer (idempotent). No DOM container needed —
     * the inquiry modal is injected dynamically by the TYPO3 theme.
     */
    init() {
        if (this.started) return;

        // Rollout guard: while the legacy standalone tracker is still
        // embedded, stay silent to avoid double tracking.
        if (document.querySelector('script[src*="native-form-tracking"]')) {
            console.info('BCE: form-tracking inactive — legacy native-form-tracking.js is still embedded');
            return;
        }

        this.started = true;

        window.addEventListener('beforeunload', this.onBeforeUnload);
        document.addEventListener('mousedown', this.onDocumentMousedown);
        document.addEventListener('click', this.onDocumentClick);

        this.observer = new MutationObserver(this.scan);
        this.observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
        });
        this.scan();
    }

    // ===== State =====

    resetState() {
        this.hasModalOpened = false;
        this.hasModalClosed = false;
        this.hasStarted = false;
        this.hasSubmitted = false;
        this.hasAbandoned = false;
        this.modalOpenedAt = 0;
        this.formStartedAt = 0;
        this.interactions = {};
        this.modalCloseReason = '';
        this.lastField = '';
        this.submitAttempts = 0;
        this.fieldSnapshot = null;
        this.clearGapTimer();
    }

    clearGapTimer() {
        if (this.gapTimer) {
            clearTimeout(this.gapTimer);
            this.gapTimer = null;
        }
    }

    elapsed(since) {
        if (!since) return undefined;
        return Math.round((Date.now() - since) / 1000);
    }

    // ===== dataLayer =====

    push(payload) {
        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(payload);
        } catch (e) {
            /* tracking must never break the page */
        }
    }

    getProduct() {
        if (!this.currentForm) return undefined;
        const input = this.currentForm.querySelector('input[name="ai[user][productCode]"]');
        return input && input.value ? input.value : undefined;
    }

    // Optional keys are explicitly reset to undefined on every push:
    // GTM merges pushes into its data layer model, so a value like
    // form_fields_filled from a previous event would otherwise stick
    // around and be sent along with later events that omit the key.

    pushModal(action, extra) {
        const payload = {
            event: 'modal',
            modal_action: action,
            modal_name: SLUG,
            modal_product: undefined,
            modal_duration: undefined,
            modal_close_reason: undefined,
            modal_interactions: undefined
        };
        const product = this.getProduct();
        if (product) payload.modal_product = product;
        this.push(Object.assign(payload, extra));
    }

    pushForm(action, extra) {
        const payload = {
            event: 'form',
            form_action: action,
            form_name: SLUG,
            form_product: undefined,
            form_duration: undefined,
            form_last_field: undefined,
            form_fields_filled: undefined,
            form_fields_empty: undefined,
            form_submit_attempts: undefined,
            client_lead_id: undefined
        };
        const product = this.getProduct();
        if (product) payload.form_product = product;
        this.push(Object.assign(payload, extra));
    }

    // ===== Lead tracking fields =====

    /**
     * Insert or update a hidden input inside the tracked form.
     * Passing an empty value removes the field so TYPO3 never
     * receives empty attribution blocks.
     */
    upsertHiddenField(name, value) {
        if (!this.currentForm) return;
        try {
            let input = this.currentForm.querySelector(`input[name="${name}"]`);
            if (!value) {
                if (input) input.remove();
                return;
            }
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                this.currentForm.appendChild(input);
            }
            input.value = value;
        } catch (e) {
            /* fail-safe */
        }
    }

    readHiddenField(name) {
        if (!this.currentForm) return undefined;
        try {
            const input = this.currentForm.querySelector(`input[name="${name}"]`);
            return input && input.value ? input.value : undefined;
        } catch (e) {
            return undefined;
        }
    }

    /** New UUID for this modal session, injected into the form POST. */
    assignLeadId() {
        this.upsertHiddenField(FIELD_LEAD_ID, generateClientLeadId());
    }

    /**
     * Snapshot consent + attribution right before the POST leaves the
     * browser. Called on every submit attempt so the payload reflects the
     * consent state at submit time.
     */
    refreshAttributionFields() {
        try {
            const consent = getMarketingConsent();
            this.upsertHiddenField(FIELD_CONSENT, consent);
            const attr = getAttribution(consent);
            this.upsertHiddenField(
                FIELD_ATTRIBUTION,
                Object.keys(attr).length ? JSON.stringify(attr) : ''
            );
        } catch (e) {
            /* fail-safe */
        }
    }

    // ===== Interaction tracking =====

    trackInteraction(name) {
        this.interactions[name] = true;
    }

    getInteractions() {
        const keys = Object.keys(this.interactions);
        return keys.length ? keys : undefined;
    }

    getFieldId(el) {
        return el.id || el.name || '';
    }

    isReportableField(el) {
        if (el.type === 'hidden' || el.type === 'submit' || el.type === 'button') return false;
        // Skip fields the user cannot see (e.g. the China-only province
        // select, whose whole container is hidden) — they would pollute
        // filled/empty reporting. Custom-UI selects (nice-select) hide the
        // native element but keep its container visible, so also accept a
        // visible parent.
        const parentVisible = el.parentElement && el.parentElement.offsetParent !== null;
        if (el.offsetParent === null && !parentVisible) return false;
        return true;
    }

    /**
     * Capture filled/empty field lists while the form is still visible.
     * Called on every interaction — submit/abandon events fire after the
     * modal is hidden, when offsetParent-based visibility checks would
     * report every field as hidden.
     */
    captureFieldSnapshot() {
        if (!this.currentForm) return;
        const filled = [];
        const empty = [];
        let anyVisible = false;
        this.currentForm.querySelectorAll('input, select, textarea').forEach((f) => {
            if (!this.isReportableField(f)) return;
            anyVisible = true;
            const id = this.getFieldId(f);
            if (!id) return;
            const hasValue = f.type === 'checkbox' || f.type === 'radio' ? f.checked : !!f.value;
            if (hasValue) {
                filled.push(id);
            } else if (f.required) {
                empty.push(id);
            }
        });
        // Form (modal) currently hidden → keep the last visible-state snapshot
        if (!anyVisible) return;
        this.fieldSnapshot = {};
        if (filled.length) this.fieldSnapshot.form_fields_filled = filled;
        if (empty.length) this.fieldSnapshot.form_fields_empty = empty;
    }

    getFieldSnapshot() {
        const result = {};
        if (this.lastField) result.form_last_field = this.lastField;
        if (this.submitAttempts > 0) result.form_submit_attempts = this.submitAttempts;
        return Object.assign(result, this.fieldSnapshot);
    }

    // ===== Event handlers =====

    onFocusIn(e) {
        this.trackInteraction('form_focus');
        const target = e.target;
        if (target && ['INPUT', 'SELECT', 'TEXTAREA'].indexOf(target.tagName) !== -1) {
            this.lastField = this.getFieldId(target);
        }
        this.captureFieldSnapshot();
        if (this.hasStarted) return;
        this.hasStarted = true;
        this.formStartedAt = Date.now();
        this.pushForm('start');
    }

    onInput() {
        this.trackInteraction('form_input');
        this.captureFieldSnapshot();
    }

    onCopy() {
        this.trackInteraction('copy');
    }

    onScroll() {
        this.trackInteraction('scroll');
    }

    onSubmitAttempt() {
        this.submitAttempts++;
        this.trackInteraction('form_submit_attempt');
        this.captureFieldSnapshot();
        // Consent + attribution snapshot must be in place before product.js
        // builds the FormData (which happens after async validation).
        this.refreshAttributionFields();
    }

    onDocumentMousedown(e) {
        // Delegated: covers the close buttons of BOTH tingle modals
        // (form modal and success modal).
        if (!this.hasModalOpened || this.hasModalClosed) return;
        if (e.target.closest && e.target.closest('.tingle-modal__close')) {
            this.modalCloseReason = 'button';
        }
    }

    onDocumentClick(e) {
        if (!this.hasModalOpened || this.hasModalClosed) return;
        const link = e.target.closest && e.target.closest('a[href]');
        if (link && !link.getAttribute('href').startsWith('#')) {
            this.modalCloseReason = 'navigation';
        }
    }

    onBeforeUnload() {
        this.fireAbandon('tab_close');
    }

    // ===== Close / abandon / success =====

    fireModalClose(reason) {
        if (!this.hasModalOpened || this.hasModalClosed) return;
        this.hasModalClosed = true;
        const extra = { modal_duration: this.elapsed(this.modalOpenedAt) };
        extra.modal_close_reason = this.modalCloseReason || reason || 'dom';
        const ia = this.getInteractions();
        if (ia) extra.modal_interactions = ia;
        this.pushModal('close', extra);
    }

    fireAbandon(reason) {
        if (this.hasStarted && !this.hasSubmitted && !this.hasAbandoned) {
            this.hasAbandoned = true;
            const extra = { form_duration: this.elapsed(this.formStartedAt) };
            Object.assign(extra, this.getFieldSnapshot());
            this.pushForm('abandon', extra);
        }
        this.fireModalClose(reason);
    }

    checkForSuccess() {
        if (this.hasSubmitted) return;
        const successEl = document.querySelector('#popup-success');
        if (!successEl) return;
        const modal = successEl.closest('.tingle-modal');
        if (modal && modal.classList.contains('tingle-modal--visible')) {
            this.hasSubmitted = true;
            this.clearGapTimer();
            this.trackInteraction('form_submit');
            const extra = { form_duration: this.elapsed(this.formStartedAt) };
            Object.assign(extra, this.getFieldSnapshot());
            // Read the id from the hidden field — guaranteed identical to
            // the value that left the browser in the form POST.
            const leadId = this.readHiddenField(FIELD_LEAD_ID);
            if (leadId) extra.client_lead_id = leadId;
            this.pushForm('submit', extra);
        }
    }

    // ===== Form attachment & scan =====

    attachToForm(form) {
        if (form === this.currentForm) return;

        if (this.currentForm) {
            this.currentForm.removeEventListener('focusin', this.onFocusIn);
            this.currentForm.removeEventListener('input', this.onInput);
            this.currentForm.removeEventListener('change', this.onInput);
        }

        this.currentForm = form;
        this.resetState();

        form.addEventListener('focusin', this.onFocusIn);
        form.addEventListener('input', this.onInput);
        form.addEventListener('change', this.onInput);

        const modal = form.closest('.tingle-modal');
        if (modal) {
            if (modal !== this.currentModal) {
                this.currentModal = modal;
                modal.addEventListener('copy', this.onCopy);
                modal.addEventListener('scroll', this.onScroll, true);
            }
            // The footer (and with it the submit button) is re-rendered on
            // every modal open — re-attach on every new form instance.
            const submitBtn = modal.querySelector(`.js-submit, [type="submit"], button[form="${form.id}"]`);
            if (submitBtn) {
                submitBtn.addEventListener('click', this.onSubmitAttempt);
            }
        }
    }

    scan() {
        const form = FORM_SELECTORS.map((s) => document.querySelector(s)).find(Boolean);
        if (!form) return;

        this.attachToForm(form);

        // --- Form modal visibility (open / reopen detection) ---
        const modal = form.closest('.tingle-modal');
        const isVisible = modal ? modal.classList.contains('tingle-modal--visible') : false;

        if (isVisible && !this.wasModalVisible) {
            this.resetState();
            this.hasModalOpened = true;
            this.modalOpenedAt = Date.now();
            this.assignLeadId();
            // Initial snapshot so the fields exist even if the submit-click
            // handler is ever bypassed; refreshed again on submit attempt.
            this.refreshAttributionFields();
            this.pushModal('open');
        }
        this.wasModalVisible = isVisible;

        // --- Server-confirmed success ---
        this.checkForSuccess();

        // --- All modals gone ---
        const anyVisible = !!document.querySelector('.tingle-modal--visible');

        if (!anyVisible && this.wasAnyModalVisible && this.hasModalOpened) {
            if (!this.hasSubmitted && document.querySelector('#popup-success')) {
                // Transition gap: product.js closed the form modal and is
                // about to open the success modal (its content is already in
                // the DOM). Not an abandon — but arm a fallback in case the
                // success modal never becomes visible.
                if (!this.gapTimer) {
                    this.gapTimer = setTimeout(() => {
                        this.gapTimer = null;
                        if (!this.hasSubmitted && !document.querySelector('.tingle-modal--visible')) {
                            this.fireAbandon('dom');
                        }
                    }, SUCCESS_GAP_TIMEOUT);
                }
            } else {
                this.fireAbandon('dom');
            }
        }
        this.wasAnyModalVisible = anyVisible;
    }
}

export default new BceFormTracking();
